import { PageTitle } from 'components/PageTitle'
import { FirebaseError } from 'firebase/app'
import { signInAnonymously } from 'firebase/auth'
import { useRepository } from 'hooks/auth'
import { useCallback, useEffect, useState } from 'react'
import { useRootStore } from 'hooks/rootStore'
import { Spinner } from 'components/Spinner'
import {
  DocumentDoesNotExistError,
  InvitationConsumeResult,
} from '@breakoutlearning/firebase-repository/types'
import { t } from 'i18next'
import { toast } from 'react-hot-toast'
import {
  noTryAsync,
  InvitationTelemetryEvent,
  errorName,
  sanitizeErrorMessage,
} from '@breakoutlearning/firebase-repository/util'

export function DemoInvitationPage() {
  const repository = useRepository()
  const rootStore = useRootStore()
  const router = rootStore.router
  const breakoutUser = repository.breakoutUser
  const invitationId = router.params?.invitationId as string | undefined
  const roomId = router.queryParams?.roomId as string | undefined
  const [step, setStep] = useState<string>('initializing')

  useEffect(() => {
    repository.logEvent(InvitationTelemetryEvent.demoAcceptPageViewed)
  }, [repository])

  const navigateToRoomOrWelcome = useCallback(async () => {
    // because solo demos have the capability to place you into a new room different from the one defined in the invitation, and which you already
    // accepted the invitation for, let's do a check to see if you can access the roomId defined in the query param, and if not just nav to demoWelcome
    // demo welcome queries for demos which you are a part of, which is what we want as opposed to sending the user to the meeting page and hard erroring out
    if (!roomId) return rootStore.navigateTo('demoWelcome')
    if (!breakoutUser) throw new Error('Breakout user not loaded')
    const [, fetchRoomError] = await noTryAsync(() =>
      breakoutUser.waitForRoomStateLoaded(roomId, { timeout: 0 })
    )
    if (!fetchRoomError) return rootStore.navigateToDemoWaitingRoom(roomId)
    rootStore.navigateTo('demoWelcome')
  }, [roomId, rootStore, breakoutUser])

  useEffect(() => {
    // We can only consume an invitation if we have an invitationId.
    if (!invitationId) {
      setStep('No invitation provided')
      return
    }

    // We only want to run authenticated logic when the user is authenticated.
    if (
      !repository.isAuthenticated ||
      !breakoutUser ||
      !breakoutUser.isLoaded
    ) {
      setStep('Waiting for user')
      return
    }

    if (breakoutUser.isCorre) {
      // If the user is an admin, we should navigate to the admin demos page.
      // if roomId is provided, provide in query param to trigger start session dialog if appropriate
      const queryParams = roomId ? { roomId } : undefined
      // we wrap this in setTimeout with 0 dur to let the current render cycle finish
      // if we don't do this, we will fail to render the component from the new route
      setTimeout(() => {
        rootStore.navigateTo('adminDemos', undefined, queryParams)
      }, 0)
      return
    }

    setStep('Consuming invitation')
    // Consume the invitation and handle the result.
    breakoutUser
      .consumeInvitation(invitationId)
      .then((response) => {
        const result = response.result
        repository.logEvent(InvitationTelemetryEvent.demoConsumeCompleted, {
          result: InvitationConsumeResult[result],
        })
        switch (result) {
          case InvitationConsumeResult.success:
            repository.logEvent(InvitationTelemetryEvent.demoAccepted)
            toast.success(t('invitation.accepted'))
            setStep('Invitation consumed successfully')
            break
          case InvitationConsumeResult.errorAlreadyConsumed:
            toast.error('Invitation already consumed')
            setStep('Invitation already consumed')
            return navigateToRoomOrWelcome()
          case InvitationConsumeResult.errorCreatedByYou:
            toast.error('Invitation created by you')
            setStep('Invitation created by you')
            return navigateToRoomOrWelcome()
          case InvitationConsumeResult.errorInvitationExpired:
            toast.error('Invitation expired')
            setStep('Invitation expired')
            return navigateToRoomOrWelcome()
          case InvitationConsumeResult.errorInvitationNotFound:
            toast.error('Invitation not found')
            setStep('Invitation not found')
            return navigateToRoomOrWelcome()
          case InvitationConsumeResult.errorPromotionAlreadyConsumed:
            toast.error('Promotion already consumed')
            setStep('Promotion already consumed')
            return navigateToRoomOrWelcome()
        }

        if (!roomId || typeof roomId !== 'string') {
          console.error('No roomId provided')
          setStep('No room provided, going home')
          // There is no room to redirect to.
          rootStore.navigateTo('demoWelcome')
          return
        }

        setStep('Waiting for room to be ready')
        breakoutUser
          .waitForRoomStateLoaded(roomId)
          .then(() => {
            // Navigate to the demo waiting room.
            setStep('Navigating to demo waiting room')
            rootStore.navigateToDemoWaitingRoom(roomId)
          })
          .catch((error) => {
            const canceled = error instanceof DocumentDoesNotExistError
            repository.logEvent(InvitationTelemetryEvent.demoRoomWaitFailed, {
              canceled,
            })
            if (canceled) {
              setStep('Demo Canceled')
              toast.error(t('demo.demo_canceled_notification'))
            } else {
              console.error('Error waiting for room', error)
              setStep('Error waiting for room')
            }
            rootStore.navigateTo('demoWelcome')
          })
      })
      .catch((error) => {
        // This catch also covers the post-consume navigation chain, so record
        // the error so consume vs navigation failures are distinguishable in BQ.
        repository.logEvent(InvitationTelemetryEvent.demoConsumeError, {
          error_name: errorName(error),
          error_message: sanitizeErrorMessage(error),
        })
        console.error('Error consuming invitation', error)
      })
  }, [
    navigateToRoomOrWelcome,
    repository.isAuthenticated,
    breakoutUser,
    breakoutUser?.isLoaded,
    repository,
    rootStore,
    invitationId,
    roomId,
  ])

  useEffect(() => {
    // Exit if the user is already authenticated.
    if (repository.isAuthenticated) {
      return
    }

    setStep('Signing in...')
    // Sign in anonymously without awaiting, as the rest of the signup process
    // is handled by a trigger and the user will become authenticated when it completes.
    // The first useEffect will run again when the user is authenticated.
    signInAnonymously(repository.auth).catch((error) => {
      console.error('Error signing in anonymously', error)
      // Security tooling on some networks strips the Referer header, which the
      // API key's referrer restriction rejects. Retrying will not help.
      const referrerBlocked =
        error instanceof FirebaseError &&
        error.code.startsWith('auth/requests-from-referer-')
      repository.logEvent(InvitationTelemetryEvent.demoAnonymousSignInFailed, {
        referrer_blocked: referrerBlocked,
      })
      setStep(referrerBlocked ? 'Sign in blocked' : 'Sign in failed')
      toast.error(
        referrerBlocked ? t('demo.sign_in_blocked') : t('demo.sign_in_failed')
      )
    })
  }, [repository.isAuthenticated, repository])

  return (
    <div className="relative box-border flex h-full w-full items-center justify-center overflow-auto rounded-3xl bg-core-tertiary">
      <PageTitle title={t('demo.page_title')} />
      <div className="flex flex-col items-center justify-center gap-2 text-center text-body-small">
        <Spinner />
        {step}
      </div>
    </div>
  )
}
